import React, { ReactElement, useState, useRef, ChangeEvent } from 'react';

import { Collection, Resource } from '../../../../types/entity.types';
import { RadioOrSelectOption } from '../../../../types/common.types';
import { Document } from '../../../../types/document.types';

import { getClassNames, openInNewTab } from '../../../../utils/util';
import { ACTIVE, ARCHIVED } from '../../../../utils/constants';

import ProgramLayout from '../../../../layouts/programme/ProgrammeLayout';
import Button from '../../../common/button/Button';
import Modal from '../../../common/modal/Modal';
import TextInput from '../../../common/input/text-input/TextInput';
import FilePicker from '../../../common/input/file-picker/FilePicker';
import ResourceViews from './resource-views/ResourceViews';
import TextArea from '../../../common/input/text-area/TextArea';
import Dropdown from '../../../common/input/dropdown/Dropdown';

import { ReactComponent as FileIcon } from '../../../../resources/icons/file.svg';
import { ReactComponent as LinkIcon } from '../../../../resources/icons/link.svg';
import { ReactComponent as PinnedIcon } from '../../../../resources/icons/pinned.svg';
import { ReactComponent as UnpinnedIcon } from '../../../../resources/icons/unpinned.svg';
import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';
import { ReactComponent as UploadIcon } from '../../../../resources/icons/upload.svg';
import { ReactComponent as PasteIcon } from '../../../../resources/icons/paste.svg';

import './plan.scss';

type Props = {
  status: string;
  collections?: Collection[];
  totalMembers: number;
  createCollection: (collection: Collection, onSaveCollectionSuccess: (externalReference: string) => void) => void;
  updateCollection: (collection: Collection, onSaveCollectionSuccess?: (externalReference: string) => void) => void;
  deleteCollection: (externalReference: string) => void;
  createResource: (collectionExternalReference: string, resource: Resource, onSaveResourceSuccess: () => void) => void;
  updateResource: (resource: Resource, onSaveResourceSuccess?: () => void) => void;
  deleteResource: (externalReference: string, uuid: string) => void;
  downloadDocument: (resource: Resource) => void;
};

const Plan = ({
  status,
  collections = [],
  totalMembers,
  createCollection,
  updateCollection,
  deleteCollection,
  createResource,
  updateResource,
  deleteResource,
  downloadDocument
}: Props): ReactElement => {
  const [showCollectionModal, setShowCollectionModal] = useState<boolean>(false);
  const [collectionExternalReference, setCollectionExternalReference] = useState<string | undefined>(undefined);
  const [collectionName, setCollectionName] = useState<string>('');
  const [collectionDescription, setCollectionDescription] = useState<string | undefined>('');
  const [showResourceModal, setShowResourceModal] = useState<boolean>(false);
  const [resourceUUID, setResourceUUID] = useState<string | undefined>(undefined);
  const [resourceName, setResourceName] = useState<string>('');
  const [resourceLink, setResourceLink] = useState<string | undefined>('');
  const [resourceFile, setResourceFile] = useState<Document | File | undefined>(undefined);
  const [resourceFileType, setResourceFileType] = useState<RadioOrSelectOption | undefined>(undefined);

  const fileRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  const resourceFileTypeOptions: React.MutableRefObject<RadioOrSelectOption[]> = useRef([
    { label: 'General', value: 'general' },
    { label: 'Meal Plan', value: 'meal-plan' }
  ]);

  const onSaveCollectionSuccess = (externalReference: string): void => {
    setCollectionExternalReference(externalReference);
    setCollectionName('');
    setCollectionDescription('');
    setShowCollectionModal(false);
    if (externalReference) {
      setShowResourceModal(true);
    }
  };

  const handleSaveCollectionClick = (): void => {
    if (collectionExternalReference) {
      const foundCollection: Collection | undefined = collections.find(
        (collection: Collection): boolean => collection.externalReference === collectionExternalReference
      );

      const collection: Collection = {
        ...foundCollection,
        name: collectionName,
        description: collectionDescription
      };
      updateCollection(collection, onSaveCollectionSuccess);
    } else {
      const collection: Collection = {
        name: collectionName,
        description: collectionDescription
      };
      createCollection(collection, onSaveCollectionSuccess);
    }
  };

  const handleCancelAddCollectionClick = (): void => {
    setShowCollectionModal(false);
    setCollectionExternalReference(undefined);
    setCollectionName('');
    setCollectionDescription('');
  };

  const onSaveResourceSuccess = (): void => {
    setShowResourceModal(false);
    setResourceName('');
    setResourceLink('');
    setResourceFile(undefined);
    setResourceFileType(undefined);
    setCollectionExternalReference(undefined);
    setResourceUUID(undefined);
  };

  const handleSaveResourceClick = async (): Promise<void> => {
    if (resourceUUID) {
      const foundCollection: Collection | undefined = collections.find((collection: Collection): boolean => {
        return collection.externalReference === collectionExternalReference;
      });

      if (!foundCollection) {
        return;
      }

      const foundResource: Resource | undefined = foundCollection?.linkedMedias?.find(
        (resource: Resource): boolean => resource.uuid === resourceUUID
      );

      if (!foundResource) {
        return;
      }

      const resource: Resource = {
        ...foundResource,
        displayName: resourceName
      };

      if (resourceFile) {
        resource.linkedMediaType = 'Document';
        resource.link = undefined;
        resource.documentType = resourceFileType?.value;
        if ((resourceFile as Document).documentExternalReference) {
          resource.documentExternalReference = (resourceFile as Document).documentExternalReference;
        } else {
          resource.document = resourceFile;
          resource.name = resourceFile.name;
        }
      } else {
        resource.linkedMediaType = 'Link';
        resource.link = resourceLink;
        resource.documentExternalReference = undefined;
      }

      updateResource(resource, onSaveResourceSuccess);
    } else {
      const resource: Resource = {
        displayName: resourceName,
        pinned: false
      };

      if (resourceFile) {
        resource.document = resourceFile;
        resource.documentType = resourceFileType?.value;
        resource.linkedMediaType = 'Document';
        resource.name = resourceFile.name;
      } else {
        resource.link = resourceLink;
        resource.linkedMediaType = 'Link';
      }

      if (!collectionExternalReference) {
        return;
      }
      createResource(collectionExternalReference, resource, onSaveResourceSuccess);
    }
  };

  const handleCancelAddResourceClick = (): void => {
    setShowResourceModal(false);
    setResourceName('');
    setResourceLink('');
    setResourceFile(undefined);
    setResourceFileType(undefined);
    setCollectionExternalReference(undefined);
    setResourceUUID(undefined);
  };

  const handleFileChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    if (!target.files) return;
    setResourceFile(target.files[0]);
    setResourceLink('');
  };

  const handleLinkChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setResourceLink(target.value);
    setResourceFile(undefined);
    setResourceFileType(undefined);
  };

  const handlePasteClick = async (): Promise<void> => {
    setResourceFile(undefined);
    setResourceFileType(undefined);
    const clipboardContents: string = await navigator.clipboard.readText();
    setResourceLink(clipboardContents ? clipboardContents : '');
  };

  const handleCollectionClick = (collection: Collection): void => {
    setCollectionExternalReference(collection.externalReference);
    setCollectionName(collection.name);
    setCollectionDescription(collection.description);
    setShowCollectionModal(true);
  };

  const handleResourceClick = (resource: Resource): void => {
    setCollectionExternalReference(resource.collectionExternalReference);
    setResourceUUID(resource.uuid);
    setResourceName(resource.displayName);

    if (resource.documentExternalReference) {
      const type: RadioOrSelectOption | undefined = resourceFileTypeOptions.current.find(
        (option: RadioOrSelectOption): boolean => option.value === resource.documentType
      );
      if (!type) {
        return;
      }
      setResourceFileType(type);
      setResourceFile({ name: resource.name ?? '', documentExternalReference: resource.documentExternalReference });
    } else {
      setResourceLink(resource.link);
    }
    setShowResourceModal(true);
  };

  const handleResourceIconClick = (resource: Resource): void => {
    if (resource.documentExternalReference) {
      downloadDocument(resource);
    } else {
      openInNewTab(resource.link);
    }
  };

  const handlePinCollectionClick = (collection: Collection): void => {
    if (status === ARCHIVED) return;

    const copyOfCollection: Collection = { ...collection };
    copyOfCollection.pinned = !copyOfCollection.pinned;

    updateCollection(copyOfCollection);
  };

  const handlePinResourceClick = (resource: Resource): void => {
    if (status === ARCHIVED) return;

    const copyOfResource: Resource = { ...resource };
    copyOfResource.pinned = !copyOfResource.pinned;

    if (copyOfResource.documentExternalReference) {
      copyOfResource.linkedMediaType = 'Document';
    } else {
      copyOfResource.linkedMediaType = 'Link';
    }

    updateResource(copyOfResource);
  };

  const renderResources = (collection: Collection): ReactElement | ReactElement[] => {
    if (!collection.linkedMedias) return <React.Fragment />;

    return collection.linkedMedias.map(
      (resource: Resource, index: number): ReactElement => (
        <div className="plan__resource" key={index}>
          <div className="plan__resource__info">
            <div className="plan__resource__icon" onClick={(): void => handleResourceIconClick(resource)}>
              {resource.documentExternalReference && <FileIcon data-testid="file-icon" />}
              {resource.link && <LinkIcon data-testid="link-icon" />}
            </div>
            <div className="plan__resource__details" onClick={(): void => handleResourceClick(resource)}>
              {resource.documentExternalReference && (
                <p className="plan__resource__name">{resource.displayName ? resource.displayName : resource.name}</p>
              )}
              {resource.link && (
                <p className="plan__resource__name">{resource.displayName ? resource.displayName : resource.link}</p>
              )}
              <p className="plan__resource__type">{resource.documentExternalReference ? 'File' : 'Link'}</p>
            </div>
          </div>
          <div className="plan__resource__actions">
            {(status === ACTIVE || status === ARCHIVED) && (
              <ResourceViews
                viewers={resource.viewers ?? []}
                memberCount={totalMembers}
                archived={status === ARCHIVED}
              />
            )}
            {resource.pinned && (
              <PinnedIcon
                className={getClassNames('plan__pinned-icon', { disabled: status === ARCHIVED })}
                data-testid="resource-pinned-icon"
                onClick={(): void => handlePinResourceClick(resource)}
              />
            )}
            {!resource.pinned && (
              <UnpinnedIcon
                className={getClassNames('plan__unpinned-icon', { disabled: status === ARCHIVED })}
                data-testid="resource-unpinned-icon"
                onClick={(): void => handlePinResourceClick(resource)}
              />
            )}
            {status !== ARCHIVED && (
              <TrashIcon
                className="plan__trash-icon"
                onClick={(): void => deleteResource(collection.externalReference ?? '', resource.uuid ?? '')}
                data-testid="resource-trash-icon"
              />
            )}
          </div>
        </div>
      )
    );
  };

  const renderCollections = (): ReactElement[] => {
    return collections.map(
      (collection: Collection, index: number): ReactElement => (
        <div className="plan__collection" key={index}>
          <div className="plan__collection__details">
            <div onClick={(): void => handleCollectionClick(collection)}>
              <p className="plan__collection__number">COLLECTION {index + 1}</p>
              <p className="plan__collection__name">{collection?.name?.toUpperCase()}</p>
            </div>
            <div className="plan__collection__actions">
              {collection.pinned && (
                <PinnedIcon
                  className={getClassNames('plan__pinned-icon', { disabled: status === ARCHIVED })}
                  onClick={(): void => handlePinCollectionClick(collection)}
                  data-testid="collection-pinned-icon"
                />
              )}
              {!collection.pinned && (
                <UnpinnedIcon
                  className={getClassNames('plan__unpinned-icon', { disabled: status === ARCHIVED })}
                  onClick={(): void => handlePinCollectionClick(collection)}
                  data-testid="collection-unpinned-icon"
                />
              )}
              {status !== ARCHIVED && (
                <TrashIcon
                  className="plan__trash-icon"
                  onClick={(): void => deleteCollection(collection.externalReference ?? '')}
                  data-testid="collection-trash-icon"
                />
              )}
            </div>
          </div>
          {collection?.description && <p className="plan__collection__description">{collection?.description}</p>}
          {renderResources(collection)}
          {status !== ARCHIVED && (
            <Button
              text="Add Resource"
              onClick={(): void => {
                setCollectionExternalReference(collection.externalReference);
                setShowResourceModal(true);
              }}
            />
          )}
        </div>
      )
    );
  };

  return (
    <ProgramLayout>
      <div className="plan" data-testid={`plan-${status}`}>
        {status !== ARCHIVED && <Button text="Add Collection" onClick={(): void => setShowCollectionModal(true)} />}
        <div
          className={getClassNames('plan__collection__list-container', {
            archived: status === ARCHIVED
          })}
        >
          {renderCollections()}
        </div>
      </div>

      <Modal show={showCollectionModal}>
        <div className="plan__modal">
          <div className="plan__modal__header">Collection {collections.length + 1}</div>
          <div className="plan__modal__input-container">
            <TextInput
              label="Collection Name:"
              name="collection-name"
              value={collectionName ?? ''}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setCollectionName(target.value)}
              disabled={status === ARCHIVED}
            />
            <TextArea
              label="Description (Optional):"
              name="collection-description"
              value={collectionDescription ?? ''}
              onChange={({ target }: ChangeEvent<HTMLTextAreaElement>): void => setCollectionDescription(target.value)}
              disabled={status === ARCHIVED}
            />
          </div>
          <div className="plan__modal__button-container">
            <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={handleCancelAddCollectionClick} />
            {status !== ARCHIVED && (
              <Button text="Save" onClick={handleSaveCollectionClick} disabled={!collectionName} />
            )}
          </div>
        </div>
      </Modal>

      <Modal show={showResourceModal}>
        <div className="plan__modal">
          <div className="plan__modal__header">Add Resource</div>
          <div className="plan__modal__input-container">
            <div className="plan__modal__resource-input-container">
              <FilePicker
                triggerElement={
                  <div className="plan__modal__resource-input">
                    <FileIcon />
                    <input
                      type="text"
                      value={resourceFile ? resourceFile.name : ''}
                      placeholder="Click button to upload"
                      disabled
                    />
                    <div className="plan__modal__resource-input__icon" onClick={(): void => fileRef?.current?.click()}>
                      <UploadIcon />
                    </div>
                  </div>
                }
                fileRef={fileRef}
                onChange={handleFileChange}
              />
              <Dropdown
                value={resourceFileType}
                options={resourceFileTypeOptions.current}
                onChange={(option: RadioOrSelectOption): void => setResourceFileType(option)}
                placeholder="Select Type..."
              />
              <span>Or</span>
              <div className="plan__modal__resource-input">
                <LinkIcon />
                <input
                  type="text"
                  value={resourceLink}
                  onChange={handleLinkChange}
                  placeholder="Paste/Type link here"
                />
                <div className="plan__modal__resource-input__icon" onClick={handlePasteClick} data-testid="paste-icon">
                  <PasteIcon />
                </div>
              </div>
            </div>
            <TextInput
              label="Custom File/Link Name (Optional):"
              name="resource-name"
              value={resourceName}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setResourceName(target.value)}
            />
          </div>
          <div className="plan__modal__button-container">
            <Button text="Cancel" onClick={handleCancelAddResourceClick} />
            <Button text="Save" onClick={handleSaveResourceClick} disabled={!resourceFile && !resourceLink} />
          </div>
        </div>
      </Modal>
    </ProgramLayout>
  );
};

export default Plan;
